import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";

export const TIMESHEET_VIEW = {
  WEEK: "week",
  MONTH: "month",
};

export const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return String(value._id || value.id || "");
  }
  return String(value);
};

export const formatDateKey = (value) => format(value, "yyyy-MM-dd");

export const normalizeDateKey = (value) => {
  if (!value) return "";

  if (value instanceof Date) {
    return formatDateKey(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const directMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (directMatch) return directMatch[1];
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return formatDateKey(parsed);
  }

  return "";
};

export const getWeekRange = (anchorDate) => {
  const start = startOfWeek(anchorDate, { weekStartsOn: 1 });
  const end = endOfWeek(anchorDate, { weekStartsOn: 1 });

  return {
    from: formatDateKey(start),
    to: formatDateKey(end),
    start,
    end,
    label: `${format(start, "dd MMM yyyy")} - ${format(end, "dd MMM yyyy")}`,
    days: eachDayOfInterval({ start, end }),
  };
};

export const getMonthRange = (anchorDate) => {
  const start = startOfMonth(anchorDate);
  const end = endOfMonth(anchorDate);

  return {
    from: formatDateKey(start),
    to: formatDateKey(end),
    start,
    end,
    label: format(start, "MMMM yyyy"),
    days: eachDayOfInterval({ start, end }),
  };
};

export const getRangeForView = (viewMode, anchorDate) =>
  viewMode === TIMESHEET_VIEW.MONTH
    ? getMonthRange(anchorDate)
    : getWeekRange(anchorDate);

export const shiftAnchorDate = (viewMode, anchorDate, direction) =>
  viewMode === TIMESHEET_VIEW.MONTH
    ? addMonths(anchorDate, direction)
    : addWeeks(anchorDate, direction);

export const formatWeekdayLabel = (value) => format(value, "EEE");

export const formatShortDateLabel = (value) => format(value, "dd MMM");

export const getDailyHoursTotals = (clients = [], days = []) =>
  days.reduce((totals, day) => {
    const dateKey = formatDateKey(day);
    totals[dateKey] = (clients || []).reduce(
      (clientTotal, client) =>
        clientTotal +
        (client.projects || []).reduce(
          (projectTotal, project) =>
            projectTotal + Number(project.dailyHours?.[dateKey] || 0),
          0
        ),
      0
    );
    return totals;
  }, {});

export const formatHours = (value) => {
  const hours = Number(value || 0);
  if (!Number.isFinite(hours)) return "0";
  return Number.isInteger(hours) ? String(hours) : String(hours);
};

export const normalizeHoursInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  return trimmed;
};

export const isValidHoursValue = (value) => {
  if (value === "") return true;
  if (typeof value !== "number" && typeof value !== "string") return false;

  const normalized = String(value).trim();
  if (!/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(normalized)) return false;

  const hours = Number(normalized);
  return Number.isFinite(hours) && hours >= 0 && hours <= 24;
};

export const buildEntryLookup = (entries) =>
  (entries || []).reduce((acc, entry) => {
    const key = buildEntryCellKey(entry.projectId, entry.workDate);
    if (!key) return acc;

    acc[key] = {
      entryId: getEntityId(entry._id || entry.id),
      hours: entry.hours,
      clientId: getEntityId(entry.clientId),
      projectId: getEntityId(entry.projectId),
      workDate: normalizeDateKey(entry.workDate),
      raw: entry,
    };
    return acc;
  }, {});

export const buildEntryCellKey = (projectId, workDate) => {
  const normalizedProjectId = getEntityId(projectId);
  const normalizedWorkDate = normalizeDateKey(workDate);

  if (!normalizedProjectId || !normalizedWorkDate) return "";

  return `${normalizedProjectId}:${normalizedWorkDate}`;
};

export const getStatusTone = (status) => {
  if (status === "saving") return "text-amber-600 dark:text-amber-300";
  if (status === "saved") return "text-emerald-600 dark:text-emerald-300";
  if (status === "error") return "text-red-600 dark:text-red-300";
  return "text-gray-400";
};

export const mergeProjectCollections = ({
  summaryProjects = [],
  fetchedProjects = [],
  days = [],
  activeProjectIds = new Set(),
  hasLoadedClientState = false,
}) => {
  const projectMap = new Map();

  fetchedProjects.forEach((project) => {
    const projectId = getEntityId(project._id || project.id);
    projectMap.set(projectId, {
      projectId,
      name: project.name || "Untitled Project",
      remark: project.remark || "",
      totalHours: 0,
      dailyHours: days.reduce((acc, day) => {
        acc[formatDateKey(day)] = 0;
        return acc;
      }, {}),
      isActive: !hasLoadedClientState || activeProjectIds.has(projectId),
      isCreating: Boolean(project.isCreating),
    });
  });

  summaryProjects.forEach((project) => {
    const projectId = getEntityId(project.projectId);
    projectMap.set(projectId, {
      projectId,
      name: project.name || "Untitled Project",
      remark: project.remark || projectMap.get(projectId)?.remark || "",
      totalHours: project.totalHours || 0,
      dailyHours: project.dailyHours || {},
      isActive: !hasLoadedClientState || activeProjectIds.has(projectId),
      isCreating: Boolean(projectMap.get(projectId)?.isCreating),
    });
  });

  return Array.from(projectMap.values()).filter((project) => project.isActive);
};

export const getMonthSummaryLabel = (range) =>
  isSameMonth(range.start, range.end)
    ? format(range.start, "MMMM yyyy")
    : `${format(range.start, "dd MMM yyyy")} - ${format(range.end, "dd MMM yyyy")}`;

export const getBangkokDateKey = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

// สถานะ submission ที่ใช้ร่วมกันทุกหน้าที่แสดงสถานะ (รวม "unlocked" ที่ admin ปลดล็อกให้แก้)
export const SUBMISSION_STATUS_LABELS = {
  "not submitted": "ยังไม่ส่ง",
  pending: "รออนุมัติ",
  approved: "อนุมัติแล้ว",
  rejected: "ไม่อนุมัติ",
  withdrawn: "ถอนการส่งแล้ว",
  unlocked: "ปลดล็อกให้แก้ไข",
};
